import type { BinaryLabel, Candle, PresetResolution, Setup } from "../types";
import {
  decisionSentence,
  outcomeSentence,
  pathSentence,
  reviewPrompt,
} from "../lib/barrierCopy";
import { excursion } from "../lib/excursion";
import { setupLineSegments } from "../lib/setupStats";
import { RevealBanner } from "./RevealBanner";
import { SetupLine } from "./SetupLine";

/**
 * The inline Reveal banner for Predict mode (spec §7, §9: inline, not a
 * modal).
 *
 * This change turned it from a report on the outcome into a review of the
 * decision. It used to say only which barrier won and how long it took, which
 * left a beginner with a result and nothing to do with it. Now, top to bottom:
 *
 *  1. the verdict, and the call the player made, with their own reason inside
 *     the same sentence when they left one;
 *  2. what price did, plainly;
 *  3. how far it went each way before resolving (`lib/excursion.ts`), because
 *     a call that was nearly home reads nothing like one price never rewarded;
 *  4. one question to take into the next round;
 *  5. the Context's setup, when there is one worth naming.
 *
 * The review line is always a question and never says what the player should
 * have done: the Forward path is hindsight, and a reveal that presents it as a
 * pattern that was there to be read teaches the wrong lesson.
 */

export type ResultBannerProps = {
  guess: BinaryLabel;
  resolution: PresetResolution;
  /** The last Context candle's close, what the barriers were struck from. */
  entry: number;
  /** The Round's Forward path, sliced here to the candles the walk saw. */
  forward: Candle[];
  note?: string;
  /** The Round's detected setup (GRA-141); absent on a pre-GRA-141 bank. */
  setup?: Setup;
  onNext: () => void;
};

/**
 * Whether the setup is worth a line. A Round with no pattern, no candle and no
 * structure to speak of would render as "Setup: no single broader structure",
 * which takes a line to say nothing; the rest of the banner is more use to the
 * player than that.
 */
function worthNaming(setup: Setup | undefined): boolean {
  if (!setup || setupLineSegments(setup) === null) return false;
  return !(setup.structure === "mixed" && !setup.pattern && !setup.candle);
}

export function ResultBanner({
  guess,
  resolution,
  entry,
  forward,
  note,
  setup,
  onNext,
}: ResultBannerProps) {
  const correct = guess === resolution.label;
  const path = excursion({
    entry,
    forward,
    daysToResolve: resolution.daysToResolve,
    guess,
    upperBarrier: resolution.upperBarrier,
    lowerBarrier: resolution.lowerBarrier,
  });

  return (
    <RevealBanner tone={correct ? "up" : "down"} onNext={onNext}>
      {/* One column of short sentences rather than a single wrapping line:
          each says its own thing, and the order is the player's call, the
          market's answer, the path, then the question. */}
      <div className="flex min-w-0 flex-col gap-y-0.5 leading-snug">
        <p className="text-fg">
          <span className={correct ? "font-bold text-up" : "font-bold text-down"}>
            {correct ? "Correct" : "Wrong"}
          </span>
          {". "}
          {decisionSentence(guess, note)}
        </p>
        <p className="text-muted">{outcomeSentence(resolution)}</p>
        <p className="text-muted">{pathSentence(path)}</p>
        {/* A hair of space before the question: the three lines above are what
            happened, this one is what to do with it. */}
        <p className="mt-1 text-dim">{reviewPrompt(correct, note)}</p>
        {worthNaming(setup) && (
          <p className="text-dim">
            <SetupLine setup={setup} />
          </p>
        )}
      </div>
    </RevealBanner>
  );
}
